import { groupBy } from 'lodash';
import { Transaction } from 'components/TransactionAllocator';
import { CalendarDay } from 'further-ui/utils';
import { TransactionOperation } from 'further-types/lp/transaction';
import { ActionTransactionSubType } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';

type FeeTransaction = {
  originalFeeId: string;
  lpFeeTemplateFeeId: string;
  lpFeeTemplateFeeName: string;
  accrualDate: CalendarDay;
  ledgerDate: CalendarDay;
  effectiveCapitalDate: CalendarDay;
  transactionTypeId: string;
  transactionSubType: string;
  operationType: TransactionOperation;
  amountIncVatTcy: number;
  amountExVatTcy: number;
  amountIncVatLcy: number;
  amountExVatLcy: number;
  salesTaxAmountLcy: number;
  commitmentId: string;
  label?: string;
};

type GroupedFeeTransactions = {
  feeId: string;
  feeName: string;
  transactions: Array<Transaction>;
};

type UseFeeTransactionsProps = {
  feeTransactions: Array<FeeTransaction>;
  baseCurrency: CurrencyCode;
  savedTransactions?: Record<string, Array<Transaction>>;
};

export const useFeeTransactions = ({
  feeTransactions,
  baseCurrency,
  savedTransactions,
}: UseFeeTransactionsProps) => {
  const grouped = groupBy(feeTransactions, 'originalFeeId');

  const groupedTransactions: Array<GroupedFeeTransactions> = Object.entries(
    grouped,
  ).map(([feeId, transactions]) => {
    const feeName = transactions[0]?.lpFeeTemplateFeeName ?? '';

    // Use saved transactions if available, otherwise use the API data
    const transactionsToUse: Array<Transaction> =
      savedTransactions?.[feeId] ??
      transactions.map((t) => ({
        id: undefined,
        actionId: t.originalFeeId,
        transactionTypeId: t.transactionTypeId,
        operationType: t.operationType,
        amountExVatLcy: t.amountExVatLcy,
        amountIncVatLcy: t.amountIncVatLcy,
        amountExVatTcy: t.amountExVatTcy,
        amountIncVatTcy: t.amountIncVatTcy,
        transactionCurrency: baseCurrency,
        transactionSubType: t.transactionSubType as ActionTransactionSubType,
        commitmentId: t.commitmentId,
        label: t.label,
        effectiveCapitalDate: t.effectiveCapitalDate,
        preCalculatedAmounts: false,
      }));

    return {
      feeId,
      feeName,
      transactions: transactionsToUse,
    };
  });

  return {
    groupedTransactions,
  };
};
